import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '@/lib/cache'
import { toCamelCase } from '@/lib/typeHelper'
import type { Vendor } from './insuranceTypes'

export enum FixKind {
  FieldEdit = 'FIELD_EDIT',
  DocumentUpload = 'DOCUMENT_UPLOAD',
  RemoveUnit = 'REMOVE_UNIT',
  CoverageChange = 'COVERAGE_CHANGE',
  Manual = 'MANUAL',
}

/** Where a proposed value came from. None means the model was not allowed to propose one. */
export enum ValueSource {
  CarrierError = 'CARRIER_ERROR',
  VinDecode = 'VIN_DECODE',
  Fmcsa = 'FMCSA',
  ExistingRecord = 'EXISTING_RECORD',
  None = 'NONE',
}

export enum FixAudience {
  Staff = 'STAFF',
  Customer = 'CUSTOMER',
  Nobody = 'NOBODY',
}

export enum FindingStatus {
  Open = 'OPEN',
  Applied = 'APPLIED',
  Dismissed = 'DISMISSED',
  Obsolete = 'OBSOLETE',
}

export enum ReportStatus {
  Pending = 'PENDING',
  Complete = 'COMPLETE',
  Failed = 'FAILED',
}

export interface FailureFinding {
  id: string
  title: string
  detail: string
  /** Every carrier this one cause is blocking — fixing it clears all of them. */
  blockedVendors: Vendor[]
  blockedIndicationIds: string[]
  fixKind: FixKind
  targetModel: string
  targetId: string | null
  targetField: string
  currentValue: string
  /** Empty unless `valueSource` names where it came from. Never a guess. */
  proposedValue: string
  valueSource: ValueSource
  audience: FixAudience
  customerPrompt: string
  status: FindingStatus
  isFixable: boolean
  dateCreated: string
  dateApplied: string | null
}

export interface FailureReport {
  id: string
  status: ReportStatus
  summary: string
  analyzedIndicationIds: string[]
  error: string
  dateCreated: string
  dateCompleted: string | null
  findings: FailureFinding[]
}

/** The newest *finished* report, plus whether a fresh run is under way behind it. */
export interface AnalysisState {
  report: FailureReport | null
  isAnalysing: boolean
}

export interface ApplyFixesResult {
  applied: string[]
  failed: { findingId: string; error: string }[]
  requote: { dispatched: Vendor[]; skipped: Vendor[]; notices?: string[] }
}

/** One fix as the dialog submits it. `value` is omitted to accept the proposal. */
export interface FixInput {
  findingId: string
  value?: string
}

function base(applicationId: string): string {
  return `/api/v1/insure/insurance-application/${applicationId}/failure-analysis`
}

const ANALYSIS_POLL_MS = 5000

export function useFailureAnalysis(applicationId?: string, enabled = true) {
  const query = useQuery({
    queryKey: [CacheKeys.FAILURE_ANALYSIS, applicationId],
    enabled: !!applicationId && enabled,
    // Only while an analysis is actually running — this panel sits open on a staff screen all day.
    refetchInterval: (query) => (query.state.data?.isAnalysing ? ANALYSIS_POLL_MS : false),
    queryFn: () =>
      axios.get(`${base(applicationId!)}/`).then((res) => toCamelCase(res.data) as AnalysisState),
  })
  return {
    report: query.data?.report ?? null,
    isAnalysing: !!query.data?.isAnalysing,
    isLoading: query.isLoading,
  }
}

export function useApplyFailureFixes(applicationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (fixes: FixInput[]) =>
      axios
        .post(`${base(applicationId)}/apply/`, {
          fixes: fixes.map((fix) => ({ finding_id: fix.findingId, value: fix.value })),
        })
        .then((res) => toCamelCase(res.data) as ApplyFixesResult),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.FAILURE_ANALYSIS, applicationId] })
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.APPLICATION] })
      // The internal tab reads the application through the org, not the detail key.
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS] })
    },
  })
}

export function useRefreshFailureAnalysis(applicationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => axios.post(`${base(applicationId)}/refresh/`).then(() => undefined),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKeys.FAILURE_ANALYSIS, applicationId] }),
  })
}

export function useDismissFailureFinding(applicationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (findingId: string) =>
      axios.post(`${base(applicationId)}/findings/${findingId}/dismiss/`).then(() => undefined),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKeys.FAILURE_ANALYSIS, applicationId] }),
  })
}
